/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DetectionStatus } from 'components/exceptions/types'

/**
 * VIS-4: action-level undo stack for Vision Exceptions.
 *
 * One entry per reviewer action — a single confirm/reject/log-issue, a
 * confirm-flow send, or a batch confirm/reject (batch counts as one
 * entry regardless of `affected.length`). The stack is capped at
 * `UNDO_STACK_MAX`; pushing past the cap drops the oldest entry.
 *
 * The slice is pure state — the actual restore (cache patch + fixture
 * mutation) happens in `undoHelpers.ts` so this file stays free of API
 * and RTKQ concerns.
 */

export const UNDO_STACK_MAX = 10

export interface UndoAffectedDetection {
  id: string
  prevStatus: DetectionStatus
}

export type UndoEntryKind = 'single' | 'batch' | 'confirm-flow'

export interface UndoEntry {
  /** Client-side unique id (incrementing counter). */
  id: string
  /** Translates to the toast / aria label for what the user is undoing. */
  description: string
  kind: UndoEntryKind
  affected: UndoAffectedDetection[]
  /** Wall-clock at push time — kept for future ordering / debugging. */
  createdAt: number
}

export interface ExceptionsUndoState {
  stack: UndoEntry[]
  /** Monotonic counter for entry ids. */
  nextEntryId: number
}

const initialState: ExceptionsUndoState = {
  stack: [],
  nextEntryId: 1,
}

const exceptionsUndoSlice = createSlice({
  name: 'exceptionsUndo',
  initialState,
  reducers: {
    pushUndoEntry(state, action: PayloadAction<Omit<UndoEntry, 'id' | 'createdAt'>>) {
      const entry: UndoEntry = {
        ...action.payload,
        id: String(state.nextEntryId),
        createdAt: Date.now(),
      }
      state.stack.push(entry)
      if (state.stack.length > UNDO_STACK_MAX) {
        state.stack.splice(0, state.stack.length - UNDO_STACK_MAX)
      }
      state.nextEntryId += 1
    },
    popUndoEntry(state) {
      state.stack.pop()
    },
    clearUndoStack(state) {
      state.stack = []
    },
  },
})

export const { pushUndoEntry, popUndoEntry, clearUndoStack } = exceptionsUndoSlice.actions

interface StateWithExceptionsUndo {
  exceptionsUndo?: ExceptionsUndoState
}

const selectUndoState = (state: StateWithExceptionsUndo): ExceptionsUndoState => state.exceptionsUndo ?? initialState

export const selectUndoStack = (state: StateWithExceptionsUndo): UndoEntry[] => selectUndoState(state).stack
export const selectCanUndo = (state: StateWithExceptionsUndo): boolean => selectUndoState(state).stack.length > 0
export const selectTopUndoEntry = (state: StateWithExceptionsUndo): UndoEntry | null => {
  const { stack } = selectUndoState(state)
  return stack.length > 0 ? stack[stack.length - 1] : null
}

export default exceptionsUndoSlice.reducer
